import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getUser } from '../../data/selectors/userAuthSelectors';
import styles from './editUser.module.css';
import { patchUser, resetUserPassword } from '../../services/orgAdmin';

const propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.number.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    last_seen: PropTypes.string,
    display_name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    access_expiration: PropTypes.string,
    organizations: PropTypes.number,
  }),
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.number.isRequired,
      org_name: PropTypes.string.isRequired,
    })
  ),
  messageCallback: PropTypes.func,
  closeCallback: PropTypes.func.isRequired,
};

export default function EditUser({ user, orgs, messageCallback, closeCallback }) {
  if (!user) return null;
  const defaultFormattedDate = user.access_expiration
    ? new Date(user.access_expiration).toISOString().substr(0, 10)
    : '';

  const [role, setRole] = useState(user.role);
  const [accessExpiration, setAccessExpiration] = useState(defaultFormattedDate);
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [displayName, setDisplayName] = useState(user.display_name);
  const [organization, setOrganization] = useState(user.organizations ?? '');
  const [resetPassword, setResetPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const admin = useSelector(getUser);
  const notify = messageCallback ?? ((msg) => console.error(msg));
  const showOrgSelect = admin.role === 'know-admin' && orgs?.length;

  const handleUserRole = ({ target }) => setRole(target.value);
  const handleAccessExpiration = ({ target }) => setAccessExpiration(target.value);
  const handleEmailChange = ({ target }) => setEmail(target.value);
  const handleFirstNameChange = ({ target }) => setFirstName(target.value);
  const handleLastNameChange = ({ target }) => setLastName(target.value);
  const handleDisplayNameChange = ({ target }) => setDisplayName(target.value);
  const handleOrganizationChange = ({ target }) => setOrganization(target.value);
  const handleCancel = () => closeCallback(null);
  const handleSubmit = () => {
    const editedUserInfo = {
      role,
      email,
      first_name: firstName,
      last_name: lastName,
      display_name: displayName,
    };
    if (accessExpiration) editedUserInfo.access_expiration = accessExpiration;
    if (user.organizations != null) editedUserInfo.source_org_id = user.organizations;
    const effectiveOrg = organization !== '' ? Number(organization) : user.organizations;
    if (effectiveOrg != null) editedUserInfo.organizations = effectiveOrg;
    patchUser(editedUserInfo, user._id)
      .then(({ data: editedUser }) => closeCallback(editedUser))
      .catch((err) => notify(err.response?.message ?? err.message));
  };
  const handleResetPassword = () => {
    const confirmation = window.confirm(
      "Reset this user's password? Their current password will stop working."
    );
    if (!confirmation) return;
    resetUserPassword(user._id)
      .then(({ data }) => {
        setResetPassword(data.password);
        setCopied(false);
      })
      .catch((err) => notify(err.response?.message ?? err.message));
  };
  const handleCopyPassword = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(resetPassword)
        .then(() => setCopied(true))
        .catch(() => document.getElementById('resetPasswordField')?.select());
    } else {
      document.getElementById('resetPasswordField')?.select();
    }
  };

  return (
    <div className={styles.backgroundDiv}>
      <div className={styles.editModal}>
        <p>Edit User</p>
        <table>
          <tbody>
            <tr>
              <td>First Name</td>
              <td>
                <input type="text" value={firstName} onChange={handleFirstNameChange} />
              </td>
            </tr>
            <tr>
              <td>Last Name</td>
              <td>
                <input type="text" value={lastName} onChange={handleLastNameChange} />
              </td>
            </tr>
            <tr>
              <td>Display Name</td>
              <td>
                <input type="text" value={displayName} onChange={handleDisplayNameChange} />
              </td>
            </tr>
            <tr>
              <td>Email</td>
              <td>
                <input type="text" value={email} onChange={handleEmailChange} />
              </td>
            </tr>
            <tr>
              <td>Role</td>
              <td>
                <select value={role} onChange={handleUserRole}>
                  <option value="regular">Regular</option>
                  <option value="org-admin">Org Admin</option>
                  {admin.organizations === 1 && <option value="allied">Allied</option>}
                  {admin.organizations === 1 && <option value="student">Student</option>}
                  {(admin.role === 'know-admin' || user.role === 'guest') && (
                    <option value="guest">Guest</option>
                  )}
                  {admin.organizations === 1 && admin.role === 'know-admin' && (
                    <option value="know-admin">Know Admin</option>
                  )}
                </select>
              </td>
            </tr>
            {showOrgSelect && (
              <tr>
                <td>Organization</td>
                <td>
                  <select value={organization} onChange={handleOrganizationChange}>
                    {organization === '' && (
                      <option value="" disabled>
                        No organization
                      </option>
                    )}
                    {orgs.map((org) => (
                      <option key={org._id} value={org._id}>
                        {org.org_name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}
            <tr>
              <td>Access Expiration</td>
              <td>
                <input type="date" value={accessExpiration} onChange={handleAccessExpiration} />
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <button type="button" onClick={handleResetPassword}>
            Reset Password
          </button>
          {resetPassword && (
            <>
              <input id="resetPasswordField" type="text" readOnly value={resetPassword} />
              <button type="button" onClick={handleCopyPassword}>
                {copied ? 'Copied!' : 'Copy Password'}
              </button>
            </>
          )}
        </div>
        <button type="button" onClick={handleCancel}>
          Cancel Edits
        </button>
        <button type="button" onClick={handleSubmit}>
          Save Edits
        </button>
      </div>
    </div>
  );
}

EditUser.propTypes = propTypes;
